/*!
 * Parallax Membership JavaScript
 * Implements smooth parallax scrolling effects
 * Beyondesign Development
 */

class ParallaxMembership {
  constructor() {
    this.sections = document.querySelectorAll('.parallax-membership');
    this.isInitialized = false;
    this.rafId = null;
    this.lastScrollY = 0;
    this.ticking = false;

    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.updateParallax = this.updateParallax.bind(this);

    this.init();
  }

  init() {
    if (this.sections.length === 0) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.disableParallax();
      return;
    }

    // Check if device is mobile/touch device
    this.isMobile = this.detectMobile();

    this.setupElements();
    this.setupFeaturesScroll();
    this.bindEvents();
    this.isInitialized = true;

    // Initial parallax update (seed & schedule next frame for smoother first paint)
    this.lastScrollY = window.pageYOffset;
    requestAnimationFrame(this.updateParallax);
  }

  detectMobile() {
    this.isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      window.innerWidth <= 768
    );
  }

  setupElements() {
    // Adjust parallax speed for mobile devices
    if (this.isMobile) {
      this.parallaxSpeed = 0.15; // Reduced from 0.2 for smoother mobile experience
    } else {
      this.parallaxSpeed = 0.5;
    }

    this.sections.forEach(section => {
      const background = section.querySelector('.parallax-membership__bg');
      const content = section.querySelector('.parallax-membership__content');

      // prevent Chrome scroll anchoring from nudging viewport (CLS guard)
      section.style.overflowAnchor = 'none'; // << added

      if (background) {
        // Ensure background is properly positioned
        background.style.willChange = 'transform';
        background.style.backfaceVisibility = 'hidden';
        background.style.perspective = '1000px';

        // Set appropriate background image based on device type
        this.setBackgroundImageForDevice(background);
      }

      if (content) {
        content.style.willChange = 'transform';
        content.style.backfaceVisibility = 'hidden';
      }
    });
  }

  setBackgroundImageForDevice(background) {
    const mobileBgUrl = background.dataset.mobileBg;
    const desktopBgUrl = background.dataset.desktopBg;

    let chosen = null;
    if (this.isMobile && mobileBgUrl) {
      chosen = mobileBgUrl;
      background.style.backgroundImage = `url('${mobileBgUrl}')`;
    } else if (desktopBgUrl) {
      chosen = desktopBgUrl;
      background.style.backgroundImage = `url('${desktopBgUrl}')`;
    }

    // Preload chosen image to avoid late decode/jump
    if (chosen) {
      const pre = new Image();
      pre.decoding = 'async';
      try { pre.fetchPriority = 'high'; } catch (e) { }
      pre.src = chosen;
    }
  }

  setupFeaturesScroll() {
    const wrappers = document.querySelectorAll('.parallax-membership__features-wrapper');
    wrappers.forEach(wrapper => {
      const originalItems = Array.from(wrapper.querySelectorAll('[data-original]'));
      if (!originalItems.length) return;

      const gap = window.innerWidth <= 768 ? 60 : 120;
      let originalWidth = 0;
      
      originalItems.forEach(item => {
        const styles = window.getComputedStyle(item);
        originalWidth += item.offsetWidth + parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
      });
      
      originalWidth += gap * (originalItems.length - 1);
      const duplications = Math.ceil(window.innerWidth * 2.5 / originalWidth) + 1;

      // Remove existing clones and create new ones
      wrapper.querySelectorAll('[data-clone]').forEach(clone => clone.remove());
      
      for (let i = 0; i < duplications; i++) {
        originalItems.forEach(item => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-clone', 'true');
          clone.removeAttribute('data-original');
          wrapper.appendChild(clone);
        });
      }

      wrapper.style.setProperty('--animation-distance', `-${originalWidth}px`);
    });
  }

  bindEvents() {
    // Use passive listeners for better performance
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // For iOS devices, also listen to touchmove events for smoother updates
    if (this.isIOS) {
      document.addEventListener('touchmove', this.handleScroll, { passive: true });
    }

    window.addEventListener('resize', this.handleResize, { passive: true });

    // Handle visibility change to pause/resume animations
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      } else if (!document.hidden && this.isInitialized) {
        this.updateParallax();
      }
    });

    // Handle BFCache restores to avoid stale transforms/jumps (iOS/Safari/Chrome)
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this.lastScrollY = window.pageYOffset;
        this.updateParallax();
      }
    }, { passive: true });
  }

  handleScroll() {
    this.lastScrollY = window.pageYOffset;

    if (!this.ticking) {
      this.rafId = requestAnimationFrame(this.updateParallax);
      this.ticking = true;
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.isMobile = this.detectMobile();
      this.parallaxSpeed = this.isMobile ? 0.15 : 0.5;

      this.sections.forEach(section => {
        const background = section.querySelector('.parallax-membership__bg');
        if (background) this.setBackgroundImageForDevice(background);
      });

      this.setupFeaturesScroll();
      this.updateParallax();
    }, 100);
  }

  updateParallax() {
    if (!this.isInitialized) return;

    const scrollY = this.lastScrollY;
    const windowHeight = window.innerHeight;

    this.sections.forEach(section => {
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top + scrollY;
      const sectionHeight = rect.height;

      // Check if section is in viewport or near it
      if (rect.bottom < -windowHeight || rect.top > windowHeight * 2) {
        return; // Skip sections that are far from viewport
      }

      const background = section.querySelector('.parallax-membership__bg');
      const content = section.querySelector('.parallax-membership__content');

      if (background) {
        // Calculate parallax offset with adjusted speed for mobile
        const yPos = (scrollY - sectionTop) * this.parallaxSpeed;

        // Apply transform with GPU acceleration
        // Use different transform approach for iOS devices
        if (this.isIOS) {
          const limitedY = Math.max(yPos, -250);
          // iOS devices need a different approach for smoother parallax
          background.style.transform = `translateY(${limitedY}px)`;
          background.style.webkitTransform = `translateY(${limitedY}px)`;
        } else {
          const limitedY = Math.max(yPos, -250);
          background.style.transform = `translate3d(0, ${limitedY}px, 0)`;
        }
      }

      if (content) {
        // Calculate content scroll progress
        const scrollProgress = Math.max(0, Math.min(1,
          (scrollY - sectionTop + windowHeight) / (sectionHeight + windowHeight)
        ));

        // Apply subtle content parallax
        const contentOffset = (scrollProgress - 0.5) * 20; // Subtle movement

        // Use different transform approach for iOS devices
        if (this.isIOS) {
          content.style.transform = `translateY(0)`;
          content.style.webkitTransform = `translateY(0)`;
        } else {
          content.style.transform = `translate3d(0, 0, 0)`;
        }

        // Update overlay opacity based on scroll
        const overlay = section.querySelector('.parallax-membership__overlay');
        if (overlay) {
          const overlayOpacity = 0.4 + (scrollProgress * 0.3); // 0.4 to 0.7
          overlay.style.opacity = Math.max(0.2, Math.min(0.8, overlayOpacity));
        }
      }
    });

    this.ticking = false;
  }

  disableParallax() {
    this.sections.forEach(section => {
      const background = section.querySelector('.parallax-membership__bg');
      const content = section.querySelector('.parallax-membership__content');

      if (background) {
        background.style.transform = 'none';
        background.style.willChange = 'auto';
      }

      if (content) {
        content.style.transform = 'none';
        content.style.willChange = 'auto';
      }
    });
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    this.disableParallax();
    this.isInitialized = false;
  }
}

// Enhanced Intersection Observer for performance
class ParallaxObserver {
  constructor() {
    this.observedElements = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const element = entry.target;
          const callback = this.observedElements.get(element);

          if (callback) {
            callback(entry.isIntersecting, entry.intersectionRatio);
          }
        });
      },
      {
        rootMargin: '800px 0px',
        threshold: [0, 0.01]
      }
    );
  }

  observe(element, callback) {
    if (!this.observer) return;

    this.observedElements.set(element, callback);
    this.observer.observe(element);
  }

  unobserve(element) {
    if (!this.observer) return;

    this.observedElements.delete(element);
    this.observer.unobserve(element);
  }
}

// Initialize when DOM is ready
function initParallaxMembership() {
  if (!window.requestAnimationFrame || !window.CSS || !CSS.supports) {
    console.warn('Parallax effects disabled: Browser not supported');
    return;
  }

  const parallax = new ParallaxMembership();
  const observer = new ParallaxObserver();

  const featureCards = document.querySelectorAll('.parallax-membership__feature-card');
  featureCards.forEach((card, index) => {
    observer.observe(card, (isVisible) => {
      if (isVisible) {
        card.style.animationDelay = `${index * 0.1}s`;
        card.classList.add('animate-in');
      }
    });
  });

  window.parallaxMembership = parallax;
  window.parallaxObserver = observer;
}

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initParallaxMembership);
} else {
  initParallaxMembership();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.parallaxMembership) {
    window.parallaxMembership.destroy();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ParallaxMembership, ParallaxObserver };
}